import { Component, effect, ElementRef, inject, OnDestroy, Renderer2, viewChild } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { MENU_COLLAPSED_CACHE_KEY, MENU_COLLAPSED_CLASS, UserPreferencesService } from '@core/services/user-preferences.service';
import { MenuService } from './app.menu.service';
import { AppSidebarComponent } from './app.sidebar.component';
import { AppTopBarComponent } from './app.topbar.component';
import { LayoutService } from './service/app.layout.service';

@Component({
  selector: 'app-layout',
  standalone: false,
  templateUrl: './app.layout.component.html'
})
export class AppLayoutComponent implements OnDestroy {
  private menuService = inject(MenuService);
  public layoutService = inject(LayoutService);
  private readonly userPreferences = inject(UserPreferencesService);
  private readonly document = inject(DOCUMENT);
  /**
   * The collapsed menu lives on <html>, not on the layout container: the shell's widths are custom properties
   * resolved at :root (premium-shell.css), so only a class there moves the menu, top bar, page and panels together.
   * Follows the saved preference, including one that arrives from the server after sign-in. Until that copy arrives
   * (or the user toggles), the value is only the default: index.html's class and its localStorage cache stay as
   * they are, or every reload would strip the collapsed menu and overwrite the cache with "wide".
   */
  private readonly applyMenuCollapsed = effect(() => {
    if (!this.userPreferences.menuCollapsedKnown()) {
      return;
    }
    const collapsed = this.userPreferences.menuCollapsed();
    this.document.documentElement.classList.toggle(MENU_COLLAPSED_CLASS, collapsed);
    try {
      localStorage.setItem(MENU_COLLAPSED_CACHE_KEY, collapsed ? '1' : '0');
    } catch {
      // Private mode: nothing to remember for the next first paint.
    }
  });
  public renderer = inject(Renderer2);
  public router = inject(Router);

  overlayMenuOpenSubscription: Subscription;
  menuOutsideClickListener: any;
  menuScrollListener: any;

  appSidebar = viewChild.required<AppSidebarComponent>(AppSidebarComponent);
  appTopbar = viewChild.required<AppTopBarComponent>(AppTopBarComponent);

  /**
   * The page scroller's rounded top clip (layout.css) has to line up with the card inside it, which sits
   * `2rem + scrollbar` inside the scroller's border box. Scrollbar width is a PLATFORM value -- 15px on
   * Windows, 0 where the OS draws overlay scrollbars -- so hardcoding it would slice 15px off the card
   * on a Mac.
   *
   * Published here rather than from a feature component because the scroller belongs to the shell: the
   * frozen quote header needs the same clip on the standalone `/quotes/quote/:id` route, where
   * `app-load-list` (which used to publish this) is not on the page at all.
   */
  private readonly contentScroll = viewChild<ElementRef<HTMLElement>>('contentScroll');
  private scrollbarRO?: ResizeObserver;
  private readonly _measureScrollbar = effect((onCleanup) => {
    const el = this.contentScroll()?.nativeElement;
    this.scrollbarRO?.disconnect();
    if (!el) return;
    const apply = () => el.style.setProperty('--workspace-scrollbar-w', `${el.offsetWidth - el.clientWidth}px`);
    apply();
    requestAnimationFrame(apply);
    // border-box: the scrollbar appears and disappears with the content, and a content-box observer
    // would not see a change that only moves the border box.
    this.scrollbarRO = new ResizeObserver(apply);
    this.scrollbarRO.observe(el, { box: 'border-box' });
    onCleanup(() => this.scrollbarRO?.disconnect());
  });

  constructor() {
    this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
      if (!this.menuOutsideClickListener) {
        this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
          const isOutsideClicked = !(
            this.appSidebar().el.nativeElement.isSameNode(event.target) ||
            this.appSidebar().el.nativeElement.contains(event.target) ||
            this.appTopbar().menuButton().nativeElement.isSameNode(event.target) ||
            this.appTopbar().menuButton().nativeElement.contains(event.target)
          );
          if (isOutsideClicked) {
            this.hideMenu();
          }
        });
      }

      if ((this.layoutService.isSlim() || this.layoutService.isSlimPlus()) && !this.menuScrollListener) {
        this.menuScrollListener = this.renderer.listen(this.appSidebar().menuContainer().nativeElement, 'scroll', () => {
          if (this.layoutService.isDesktop()) {
            this.hideMenu();
          }
        });
      }

      if (this.layoutService.state.staticMenuMobileActive) {
        this.blockBodyScroll();
      }
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.hideMenu();
      // The scroll container survives route changes, so opening a record mid-scroll used to land
      // the new page wherever the old one left off. Every navigation starts at the top; flows that
      // deliberately preserve position (QuoteLoadBridge's in-place reload) re-assert theirs per
      // frame and win over this single reset.
      document.getElementById('layout-content-router-outlet')?.scrollTo({ top: 0, behavior: 'auto' });
    });

  }

  blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  }

  unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replace(new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
    }
  }

  hideMenu() {
    this.layoutService.state.overlayMenuActive = false;
    this.layoutService.state.staticMenuMobileActive = false;
    this.layoutService.state.menuHoverActive = false;
    this.menuService.reset();
    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }

    if (this.menuScrollListener) {
      this.menuScrollListener();
      this.menuScrollListener = null;
    }

    this.unblockBodyScroll();
  }

  get containerClass() {
    return {
      'layout-slim': this.layoutService.config.menuMode === 'slim',
      'layout-slim-plus': this.layoutService.config.menuMode === 'slim-plus',
      // Desktop draws the slim-plus menu wide: icons with labels beside them (premium-shell.css, Dan 2026-10-16).
      // Collapsed, it falls back to the original 7rem icon rail with fly-out groups.
      'layout-wide': this.layoutService.config.menuMode === 'slim-plus' && !this.userPreferences.menuCollapsed(),
      'layout-static': this.layoutService.config.menuMode === 'static',
      'layout-overlay': this.layoutService.config.menuMode === 'overlay',
      'layout-overlay-active': this.layoutService.state.overlayMenuActive,
      'layout-mobile-active': this.layoutService.state.staticMenuMobileActive,
      'layout-static-inactive': this.layoutService.state.staticMenuDesktopInactive && this.layoutService.config.menuMode === 'static',
      'p-input-filled': this.layoutService.config.inputStyle === 'filled',
      'p-ripple-disabled': !this.layoutService.config.ripple,
      'layout-light': this.layoutService.config.layoutTheme === 'colorScheme' && this.layoutService.config.colorScheme === 'light',
      'layout-dark': this.layoutService.config.layoutTheme === 'colorScheme' && this.layoutService.config.colorScheme === 'dark',
      'layout-primary': this.layoutService.config.colorScheme !== 'dark' && this.layoutService.config.layoutTheme === 'primaryColor'
    };
  }

  ngOnDestroy() {
    if (this.overlayMenuOpenSubscription) {
      this.overlayMenuOpenSubscription.unsubscribe();
    }

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
    }
  }
}
